import React, { useState, useEffect } from 'react';

import { Response } from 'src/api/types';
import { useDataContext } from 'src/context/data';
import isEmpty from 'lodash/isEmpty';
import { motion } from 'framer-motion';

import { useCapturePersonalDataIsAllowed } from 'src/utils/hooks';
import { getDataFromSessionStorage, KEYS } from 'src/utils/persistentStorage';
import analytics from 'src/utils/analytics/analytics';

import CapturePersonalDataForm from './CapturePersonalDataForm/CapturePersonalDataForm';

import { createBlobs } from './utils';

import { renderShortArrowIcon } from 'src/utils/iconRenderers';
import arrowNextIcon from '../../../../assets/right-arrow.svg';
import { renderHomeIcon, renderInfoIcon } from 'src/utils/iconRenderers';

import { childAnimationVariants, parentAnimationVariants } from './animations';

import colorsModule from 'src/utils/styles/_colors.module.scss';

const colors = colorsModule.locals;

/* - - - - - - - - - - - - - - - - - - - - - - */

function Responses({ onResponsesAnimationComplete }) {
  const { appState, goForward, setResponsesCurrentBlobInfo, goHome, setResponsesGroupHidden } = useDataContext();
  const {
    routerHistory,
    widgetData: { config, parameters },
    currentMessageId,
    isResponsesGroupHide,
    responsesCurrentBlobInfo: { index },
  } = appState;

  const capturePersonalDataIsAllowed = useCapturePersonalDataIsAllowed();

  const [capturePersonalDataFormIsVisible, setCapturePersonalDataFormIsVisible] =
    useState(capturePersonalDataIsAllowed);
  const [isPDCFormSentMessageIsVisible, setPDCFormSentMessageIsVisible] = useState(false);

  useEffect(() => {
    setCapturePersonalDataFormIsVisible(capturePersonalDataIsAllowed);
  }, [capturePersonalDataIsAllowed]);

  useEffect(() => {
    onResponsesAnimationComplete(capturePersonalDataFormIsVisible);
  }, [capturePersonalDataFormIsVisible]);

  useEffect(() => {
    if (capturePersonalDataIsAllowed) {
      const isFormSubmitted = getDataFromSessionStorage(KEYS.isPDCFormSubmitted);

      setCapturePersonalDataFormIsVisible(!isFormSubmitted);
      setPDCFormSentMessageIsVisible(isFormSubmitted);
    } else {
      setPDCFormSentMessageIsVisible(false);
    }
  }, [currentMessageId]);

  const blobs = createBlobs(config.script[currentMessageId].quickResponses);

  const renderResponseItem = (response: Response, i: number) => {
    const handleResponseClick = () => {
      analytics.trackResponseButtonPressed({ responseLabel: response.label, responseValue: response.value });
      goForward(response.value);
    };

    return (
      <motion.div
        key={response.id}
        id={'responseId' + response.id}
        className="rcw-response-button"
        style={{
          borderColor: parameters.isUserBorderEnabled ? parameters.titleBackgroundColor : 'transparent',
          background: parameters.chatOptionButtonBackgroundColor,
          boxShadow: parameters.isUserShadowEnabled ? colors.boxShadow : 'none',
          fontSize: parameters.fontSize + 'px',
          color: parameters.chatOptionButtonTextColor,
        }}
        onClick={handleResponseClick}
        variants={childAnimationVariants}
        data-cy="response-button"
      >
        <span>{response.label}</span>
        {renderShortArrowIcon(parameters.chatOptionButtonTextColor)}
      </motion.div>
    );
  };

  const renderPrevNavButton = index > 0;
  const renderNextNavButton = index < blobs.length - 1;

  const renderBlobs = (blobs: Array<Array<Response>>) => {
    return blobs.map((blob, i) => (
      <motion.div
        key={i}
        className="rcw-response-group"
        data-cy="responses-active-group"
        variants={parentAnimationVariants}
        initial="closed"
        animate={i === index ? 'open' : 'closed'}
        onAnimationStart={() => onResponsesAnimationComplete(false)}
        onAnimationComplete={() => onResponsesAnimationComplete(true)}
      >
        {i === index && blob.map(renderResponseItem)}
      </motion.div>
    ));
  };

  const renderBackToHomeButton = () => {
    if (routerHistory.length === 1) {
      return null;
    }

    const handleBackToHomeClick = () => {
      analytics.trackBackToHomeButtonPressed();
      goHome();
    };

    return (
      <div className="rcw-back-to-home-button-holder" data-cy="back-to-home-button">
        <button className="rcw-nav-back-to-home-button" onClick={handleBackToHomeClick}>
          {renderHomeIcon()}
          <span>Back to Home</span>
        </button>
      </div>
    );
  };

  const renderResponsesForm = () => {
    if (isResponsesGroupHide) {
      const handleBackFromPageClick = () => {
        setResponsesGroupHidden(false);
        analytics.trackBackFromPageButtonPressed();
      };
      return (
        <div className="rcw-responses-container" data-cy="responses-container">
          <div className="rcw-responses-wrapper">
            <motion.div
              className="rcw-response-button rcw-response-back-button"
              style={{
                borderColor: parameters.isUserBorderEnabled ? parameters.titleBackgroundColor : 'transparent',
                background: parameters.chatOptionButtonBackgroundColor,
                boxShadow: parameters.isUserShadowEnabled ? colors.boxShadow : 'none',
                fontSize: parameters.fontSize + 'px',
                color: parameters.chatOptionButtonTextColor,
              }}
              variants={childAnimationVariants}
              data-cy="response-back-button"
              onClick={handleBackFromPageClick}
            >
              Back
            </motion.div>
          </div>
        </div>
      );
    }

    return (
      <>
        {isPDCFormSentMessageIsVisible && (
          <div className="rcw-pdc-form-was-sent">
            {renderInfoIcon(colors.text)}
            You have already filled out this form. If you have not already received an email from us check your Spam
            folder.
          </div>
        )}
        {!isEmpty(blobs) ? (
          <div className="rcw-responses-container" data-cy="responses-container">
            <div className="rcw-responses-wrapper">
              {renderPrevNavButton && (
                <button
                  className="rcw-nav-responses-group-button prev"
                  onClick={() => setResponsesCurrentBlobInfo(index - 1, blobs[index - 1].length)}
                  data-cy="prev-responses-button"
                >
                  <img src={arrowNextIcon} alt="Prev responses" />
                </button>
              )}
              {renderBlobs(blobs)}
              {renderNextNavButton && (
                <button
                  className="rcw-nav-responses-group-button next"
                  onClick={() => setResponsesCurrentBlobInfo(index + 1, blobs[index + 1].length)}
                  data-cy="next-responses-button"
                >
                  <img src={arrowNextIcon} alt="Next responses" />
                </button>
              )}
            </div>
          </div>
        ) : (
          renderBackToHomeButton()
        )}
      </>
    );
  };

  return capturePersonalDataFormIsVisible ? (
    <CapturePersonalDataForm onDone={() => setCapturePersonalDataFormIsVisible(false)} />
  ) : (
    renderResponsesForm()
  );
}

export default Responses;
